/**
 * Cart Zero Quantity Removal Handler
 * Handles automatic item removal when quantity reaches zero
 */

class CartZeroRemoval {
  constructor() {
    this.init();
  }

  init() {
    console.log('CartZeroRemoval initialized');
    this.bindEvents();
  }

  bindEvents() {
    // Handle quantity input changes
    document.addEventListener('change', (event) => {
      if (this.isCartQuantityInput(event.target)) {
        this.handleQuantityChange(event);
      }
    });

    // Handle minus button clicks
    document.addEventListener('click', (event) => {
      if (this.isMinusButton(event.target)) {
        this.handleMinusClick(event);
      }
    });

    // Handle remove button clicks
    document.addEventListener('click', (event) => {
      if (this.isRemoveButton(event.target)) {
        this.handleRemoveClick(event);
      }
    });

    // Handle plus button clicks (prevent negative quantities)
    document.addEventListener('click', (event) => {
      if (this.isPlusButton(event.target)) {
        this.handlePlusClick(event);
      }
    });
  }

  isCartQuantityInput(element) {
    return element.matches('#CartDrawer input[type="number"]') ||
           element.matches('.cart-drawer input[type="number"]') ||
           element.matches('input[data-quantity-variant-id]');
  }

  isMinusButton(element) {
    const button = element.matches('button') ? element : element.closest('button');
    return button && (
      button.matches('#CartDrawer button[name="minus"]') ||
      button.matches('.cart-drawer button[name="minus"]')
    );
  }

  isRemoveButton(element) {
    const button = element.matches('button') ? element : element.closest('button');
    return button && (
      button.matches('#CartDrawer .cart-remove-button') ||
      button.matches('.cart-drawer .cart-remove-button') ||
      button.classList.contains('cart-remove-button')
    );
  }

  isPlusButton(element) {
    const button = element.matches('button') ? element : element.closest('button');
    return button && (
      button.matches('#CartDrawer button[name="plus"]') ||
      button.matches('.cart-drawer button[name="plus"]')
    );
  }

  handleQuantityChange(event) {
    const input = event.target;
    const quantity = parseInt(input.value);
    const variantId = this.getVariantId(input);
    const itemElement = this.getItemElement(input);

    console.log('Quantity changed to:', quantity, 'for variant:', variantId);

    if (quantity <= 0 && variantId && itemElement) {
      event.preventDefault();
      this.removeItem(variantId, itemElement);
    }
  }

  handleMinusClick(event) {
    const button = event.target.matches('button') ? event.target : event.target.closest('button');
    const quantityInput = button.parentElement.querySelector('input[type="number"]');
    const currentQuantity = parseInt(quantityInput.value);
    const variantId = this.getVariantId(button, quantityInput);
    const itemElement = this.getItemElement(button);

    console.log('Minus clicked. Current quantity:', currentQuantity, 'Variant:', variantId);

    if (currentQuantity <= 1 && variantId && itemElement) {
      event.preventDefault();
      event.stopPropagation();
      this.removeItem(variantId, itemElement);
    }
  }

  handleRemoveClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.target.matches('button') ? event.target : event.target.closest('button');
    const variantId = this.getVariantId(button);
    const itemElement = this.getItemElement(button);

    console.log('Remove clicked for variant:', variantId);

    if (variantId && itemElement) {
      this.removeItem(variantId, itemElement);
    }
  }

  handlePlusClick(event) {
    const button = event.target.matches('button') ? event.target : event.target.closest('button');
    const quantityInput = button.parentElement.querySelector('input[type="number"]');

    // Ensure minimum value is 1 when increasing
    if (parseInt(quantityInput.value) <= 0) {
      quantityInput.value = 1;
    }
  }

  getVariantId(element, fallbackElement = null) {
    // Try multiple approaches to get variant ID
    const variantId = element.getAttribute('data-variant-id') ||
                     element.getAttribute('data-quantity-variant-id') ||
                     (fallbackElement && fallbackElement.getAttribute('data-quantity-variant-id')) ||
                     (fallbackElement && fallbackElement.getAttribute('data-variant-id'));
                     
    return variantId;
  }

  getItemElement(element) {
    return element.closest('.cart-item') ||
           element.closest('[id*="CartDrawer-Item-"]') ||
           element.closest('[id*="CartItem-"]');
  }

  async removeItem(variantId, itemElement) {
    console.log('Removing item:', variantId);

    try {
      // Show loading state
      this.showLoading(itemElement);

      // Remove item via API
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cart = await response.json();
      console.log('Item removed successfully. Cart:', cart);

      // Refresh cart drawer
      await this.refreshCartDrawer(cart);

    } catch (error) {
      console.error('Error removing item:', error);
      this.handleError(itemElement, error);
    }
  }

  showLoading(itemElement) {
    // Show loading overlay
    const loadingOverlay = itemElement.querySelector('.loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.style.display = 'block';
    }

    // Disable controls
    const controls = itemElement.querySelectorAll('button, input');
    controls.forEach(control => control.disabled = true);
  }

  hideLoading(itemElement) {
    // Hide loading overlay
    const loadingOverlay = itemElement.querySelector('.loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.style.display = 'none';
    }

    // Re-enable controls
    const controls = itemElement.querySelectorAll('button, input');
    controls.forEach(control => control.disabled = false);
  }

  handleError(itemElement, error) {
    this.hideLoading(itemElement);

    // Reset quantity to 1
    const quantityInput = itemElement.querySelector('input[type="number"]');
    if (quantityInput) {
      quantityInput.value = Math.max(1, parseInt(quantityInput.value) || 1);
    }

    // Show error message
    alert('Error removing item. Please try again.');
  }

  async refreshCartDrawer(cart) {
    try {
      const response = await fetch('/cart?section_id=cart-drawer');
      const html = await response.text();
      
      const parser = new DOMParser();
      const newDoc = parser.parseFromString(html, 'text/html');
      const newCartDrawer = newDoc.querySelector('#CartDrawer');
      const currentCartDrawer = document.querySelector('#CartDrawer');

      if (newCartDrawer && currentCartDrawer) {
        // Replace content
        currentCartDrawer.innerHTML = newCartDrawer.innerHTML;

        // Update cart count
        this.updateCartCount(cart.item_count);

        // Close drawer if empty
        if (cart.item_count === 0) {
          setTimeout(() => {
            this.closeCartDrawer();
          }, 1000);
        }

        // Dispatch custom event
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart }
        }));
      }
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
      // Fallback: reload page
      window.location.reload();
    }
  }

  updateCartCount(count) {
    // Update all cart count elements
    const selectors = [
      '.cart-count-bubble span',
      '[data-cart-count]',
      '#shoppingCart span',
      '.cart-icon span'
    ];

    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        if (element.closest('#shoppingCart')) {
          element.textContent = `(${count})`;
        } else {
          element.textContent = count.toString();
        }
      });
    });

    // Show/hide cart bubbles
    const bubbles = document.querySelectorAll('.cart-count-bubble');
    bubbles.forEach(bubble => {
      bubble.style.display = count > 0 ? '' : 'none';
    });
  }

  closeCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    
    if (cartDrawer && typeof cartDrawer.close === 'function') {
      cartDrawer.close();
    } else {
      // Fallback methods
      const closeBtn = document.querySelector('#CartDrawer .btn-close');
      if (closeBtn) {
        closeBtn.click();
      } else {
        // Bootstrap offcanvas close
        const offcanvas = document.querySelector('#CartDrawer.offcanvas');
        if (offcanvas && window.bootstrap) {
          const bsOffcanvas = bootstrap.Offcanvas.getInstance(offcanvas);
          if (bsOffcanvas) {
            bsOffcanvas.hide();
          }
        }
      }
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartZeroRemoval();
  });
} else {
  new CartZeroRemoval();
}

// Also initialize on Shopify section loads (for themes that reload sections)
document.addEventListener('shopify:section:load', () => {
  new CartZeroRemoval();
});

// Export for use in other scripts if needed
window.CartZeroRemoval = CartZeroRemoval;